import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';

interface SellerGuideStructuredDataProps {
  canonicalUrl?: string;
}

export default function SellerGuideStructuredData({
                                                    canonicalUrl = 'https://spiritory.com/seller-guide'
                                                  }: SellerGuideStructuredDataProps) {
  const { t } = useTranslation('seller_guide');
  const { t: tCommon } = useTranslation('common');

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        "url": canonicalUrl,
        "name": translationFallback(
          'seller_guide_title',
          tCommon('seller_guide_title'),
          'How to Sell Whisky – Seller Guide | Spiritory'
        ),
        "description": translationFallback(
          'seller_guide_description',
          tCommon('seller_guide_description'),
          'Learn how to list and sell your whisky bottles on Spiritory\'s secure, digital marketplace in just a few simple steps.'
        ),
        "isPartOf": {
          "@id": "https://spiritory.com/#website"
        },
        "inLanguage": "en-US",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://spiritory.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Seller Guide",
              "item": canonicalUrl
            }
          ]
        },
        "datePublished": "2024-09-15",
        "dateModified": "2025-07-17"
      },
      {
        "@type": "HowTo",
        "name": translationFallback(
          'how_to_sell_whisky_title',
          t('how_to_sell_whisky_title'),
          'How to Sell Whisky on Spiritory'
        ),
        "description": translationFallback(
          'seller_guide_description',
          t('seller_guide_description'),
          'Learn how to list and sell your whisky bottles on Spiritory\'s secure, digital marketplace in just a few simple steps.'
        ),
        "image": {
          "@type": "ImageObject",
          "url": "https://spiritory.com/images/seller-guide/seller-guide-hero.webp",
          "width": 800,
          "height": 600
        },
        "totalTime": "PT10M",
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": "EUR",
          "value": 0
        },
        "supply": [
          {
            "@type": "HowToSupply",
            "name": "Spiritory user account"
          },
          {
            "@type": "HowToSupply",
            "name": "Whisky bottle to sell"
          },
          {
            "@type": "HowToSupply",
            "name": "High-quality photos of the bottle"
          }
        ],
        "step": [
          {
            "@type": "HowToStep",
            "name": "Finding Your Whisky Bottle to sell",
            "text": translationFallback(
              'step_finding_bottle_sell',
              t('step_finding_bottle_sell'),
              "Simply explore our growing marketplace and select the bottle you want to sell."
            ),
            "position": 1
          },
          {
            "@type": "HowToStep",
            "name": "The Spiritory Product Page",
            "text": translationFallback(
              'step_product_page_sell',
              t('step_product_page_sell'),
              "Once you find the desired bottle, you can decide how to sell it. Your two options are 'sell now' or 'Ask offer."
            ),
            "position": 2
          },
          {
            "@type": "HowToStep",
            "name": "The Spiritory Product Page: Sell now",
            "text": translationFallback(
              'step_sell_now',
              t('step_sell_now'),
              "Sell instantly at the highest 'Bid' of a buyer."
            ),
            "position": 3
          },
          {
            "@type": "HowToStep",
            "name": "The Spiritory Product Page: Place an Ask",
            "text": translationFallback(
              'step_place_ask',
              t('step_place_ask'),
              "When placing an ask, you decide the price you're selling at."
            ),
            "position": 4
          },
          {
            "@type": "HowToStep",
            "name": "Successfully sold a whisky bottle at Spiritory",
            "text": translationFallback(
              'step_successful_sale',
              t('step_successful_sale'),
              "Then you need Authenticate your bottle and shipped."
            ),
            "position": 5
          },
          {
            "@type": "HowToStep",
            "name": "Get your payout with Spiritory",
            "text": translationFallback(
              'step_get_payout',
              t('step_get_payout'),
              "You will receive your money automatically, when the buyer has confirmed the safe arrival."
            ),
            "position": 6
          }
        ],
        "datePublished": "2024-09-15",
        "dateModified": "2025-07-17"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_why_sell_q',
              t('faq_why_sell_q'),
              'Why Sell Whisky and Spirits on Spiritory? Benefits for Individual and Professional Sellers'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_why_sell_a',
                t('faq_why_sell_a'),
                'Selling on Spiritory gives both private collectors and professional merchants access to a global premium spirits marketplace. You benefit from fair pricing, fast payouts, expert bottle authentication, and zero listing fees. Whether you\'re selling a single bottle or an entire inventory, Spiritory makes it easy to reach whisky buyers worldwide.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_authentication_works_q',
              t('faq_authentication_works_q'),
              'How Bottle Authentication Works and What Quality Standards We Follow'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_authentication_works_a',
                t('faq_authentication_works_a'),
                'Every bottle sold on Spiritory goes through a rigorous multi-step authentication process by industry experts. We inspect fill level, label condition, seal integrity, and packaging to ensure authenticity. Only bottles that meet our high quality standards are approved—giving buyers complete confidence and sellers a trusted platform.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_requirements_rare_q',
              t('faq_requirements_rare_q'),
              'Requirements for Selling Rare and Collectible Whisky Bottles'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_requirements_rare_a',
                t('faq_requirements_rare_a'),
                'To sell rare or collectible whisky on Spiritory, your bottle must be unopened, sealed, and in excellent condition. Limited editions, age-statement whiskies, and discontinued releases are accepted if they meet our visual and packaging standards. Clear photos and accurate descriptions are required to list collectible bottles successfully.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_ask_duration_q',
              t('faq_ask_duration_q'),
              'How long does my Ask (whisky listing) stay active on Spiritory marketplace?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_ask_duration_a',
                t('faq_ask_duration_a'),
                'Your Ask lasts for 60 days, but you can also choose other periods for your Asks.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_cancel_ask_q',
              t('faq_cancel_ask_q'),
              'How do I cancel my Ask (whisky sale listing) before it sells?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_cancel_ask_a',
                t('faq_cancel_ask_a'),
                'Simply go to \'Sell,\' to your \'open Asks,\' and delete your Ask.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_edit_ask_q',
              t('faq_edit_ask_q'),
              'Can I edit my Ask(whisky sale listing) price and details after posting?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_edit_ask_a',
                t('faq_edit_ask_a'),
                'Yes, you can! Simply log in to your account, go to your active offers, and select the offer you want to customize. From there, you can make any changes and update the listing.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_multiple_bottles_q',
              t('faq_multiple_bottles_q'),
              'How many whisky bottles can I sell simultaneously on Spiritory?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_multiple_bottles_a',
                t('faq_multiple_bottles_a'),
                'on our platform you can offer several bottles at the same time. Whether you\'re a private or commercial seller, listing multiple bottles is easy and allows you to display your entire range at once.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_payment_when_q',
              t('faq_payment_when_q'),
              'When and how do I receive payment after selling whisky on Spiritory?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_payment_when_a',
                t('faq_payment_when_a'),
                'Payments are initiated 3–5 business days after the buyer confirms receipt and the transaction is marked as complete. The amount is securely transferred to the bank account linked to your seller profile.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_add_new_bottle_q',
              t('faq_add_new_bottle_q'),
              'How do I add a new whisky bottle that\'s not listed on Spiritory marketplace?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_add_new_bottle_a',
                t('faq_add_new_bottle_a'),
                'If the bottle you are looking for is missing from our marketplace, you can suggest it to us quickly and easily! Just follow these steps:Log in: Log in to your Spiritory account.Suggest a bottle: Go to the page and click on Suggest a product. Here you can enter all the information about the missing bottle.The following fields must be filled in:Product category: Select the appropriate category, e.g. Whisky.Product Name: Enter the full name of the bottle.Brand: Select an existing brand or add a new one.Optional Information: You can add the following details to help us consider your proposal:Year Bottled / VintageRetail Price (RRP): Please, rounded without decimals.Release Date: Format: DD/MM/YYYY.External link: If you have a link to another website with more information, you can provide it here.Once you\'ve entered everything, our team will review the suggestion and add the bottle as soon as possible.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_authentication_time_q',
              t('faq_authentication_time_q'),
              'How long does whisky bottle authentication take and what\'s the verification process with Spiritory?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_authentication_time_a',
                t('faq_authentication_time_a'),
                'At Spiritory, our goal is to offer you the highest level of security and confidence when buying your bottle – just like buying from a liquor store. Here is our authentication process:AuthenticationEach product is verified by our team of experts to ensure that it is authentic and free of counterfeits. This protects you as a buyer and guarantees that only genuine bottles are sold.Condition of the bottle and packagingWe make sure that both the bottle and the packaging are in perfect condition. Our team carefully examines these aspects to ensure that you receive a high-quality product. Submitting the imagesTo authenticate the bottle, our team needs images of the bottle and its packaging. These images help evaluate the authenticity and condition of the product.Review by the team of expertsAfter the images have been submitted, our team of experts will begin the review. Within 48 hours, you will receive feedback as to whether the authentication was successful.Payment and shippingAfter successful authentication, you can make your payment. The seller will then receive the shipping information, and the bottle will be shipped to you.This process provides you with a high level of security and ensures that you only get genuine and flawless bottles on Spiritory.'
              )
            }
          }
        ]
      },
      {
        "@type": "Organization",
        "@id": "https://spiritory.com/#organization",
        "name": "Spiritory",
        "url": "https://spiritory.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://spiritory.com/images/logo.png",
          "width": 200,
          "height": 60
        },
        "sameAs": [
          "https://www.facebook.com/spiritory",
          "https://twitter.com/spiritory",
          "https://www.instagram.com/spiritory"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+34-XXX-XXX-XXX",
          "contactType": "customer service",
          "areaServed": "ES",
          "availableLanguage": [
            "Spanish", "English", "French", "Italian", "German"
          ]
        }
      }
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }}
    />
  );
}